import { useState } from 'react';
import { GripVertical, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { AnalysisModel } from '../../lib/analysis-models';
import {
  AnalysisModelDraft,
  useAllAnalysisModels,
  useCreateAnalysisModel,
  useDeleteAnalysisModel,
  useUpdateAnalysisModel,
} from '../../queries/analysisModels';
import { AnalysisModelFormDialog } from './AnalysisModelFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Checkbox } from '../../components/ui/Checkbox';

const EMPTY_DRAFT: AnalysisModelDraft = {
  label: '',
  ai_model: '',
  ai_version: '',
  webhook_path: '',
  default_slot: 1,
  sort_order: 100,
  is_preferred: false,
  no_market: false,
  is_active: true,
  notes: '',
};

export function AnalysisModelsPage() {
  const { data: models = [], isLoading } = useAllAnalysisModels();
  const create = useCreateAnalysisModel();
  const update = useUpdateAnalysisModel();
  const remove = useDeleteAnalysisModel();
  const { success, error } = useToast();

  const [editing, setEditing] = useState<AnalysisModelDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AnalysisModel | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!editing) return;
    if (!editing.label || !editing.webhook_path) {
      setFormError('Label and webhook path are required.');
      return;
    }
    setFormError(null);
    const isEdit = !!editing.id;
    const onSettled = {
      onSuccess: () => {
        setEditing(null);
        success(isEdit ? 'Model updated.' : 'Model created.');
      },
      onError: (err: unknown) => setFormError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const toggle = (m: AnalysisModel, fields: Partial<AnalysisModel>) =>
    update.mutate({ id: m.id, ...fields }, { onError: (err: unknown) => error(extractErrorMessage(err, 'Update failed.')) });

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.label}".`);
        setDeleting(null);
      },
      onError: (err: unknown) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  // Drag-and-drop reorder: move the dragged row to `to`, renumber sort_order
  // in steps of 10, and persist only the rows whose sort_order actually changed.
  const onDrop = (to: number) => {
    const from = dragIndex;
    setDragIndex(null);
    if (from === null || from === to) return;
    const next = [...models];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    next.forEach((m, i) => {
      const sortOrder = (i + 1) * 10;
      if (m.sort_order !== sortOrder) update.mutate({ id: m.id, sort_order: sortOrder });
    });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Analysis Models"
        description="Models the run picker can launch. Each has its own n8n webhook path. Drag to reorder."
        actions={
          <Button onClick={() => setEditing({ ...EMPTY_DRAFT })}>
            <Plus className="h-4 w-4" />
            Add model
          </Button>
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="w-8 bg-muted/50 px-2 py-2.5"></th>
                <th className="bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Label</th>
                <th className="bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Model / version</th>
                <th className="bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Webhook path</th>
                <th className="bg-muted/50 px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Slot</th>
                <th className="bg-muted/50 px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred</th>
                <th className="bg-muted/50 px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Active</th>
                <th className="bg-muted/50 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {models.map((m, idx) => (
                <tr
                  key={m.id}
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                  className={`transition-colors hover:bg-muted/40 ${m.is_active ? '' : 'opacity-50'} ${dragIndex === idx ? 'opacity-40' : ''}`}
                >
                  <td className="w-8 cursor-grab px-2 py-2.5 text-muted-foreground active:cursor-grabbing">
                    <GripVertical className="h-4 w-4" />
                  </td>
                  <td className="px-3 py-2.5 font-medium">
                    {m.is_preferred && <Star className="mr-1 inline h-3.5 w-3.5 text-amber-500" />}
                    {m.label}
                    {m.no_market && <Badge variant="outline" className="ml-1.5">no mkt</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{[m.ai_model, m.ai_version].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{m.webhook_path}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums">{m.default_slot}</td>
                  <td className="px-3 py-2.5 text-center">
                    <Checkbox
                      checked={m.is_preferred}
                      onChange={(e) => toggle(m, { is_preferred: e.target.checked })}
                      aria-label={`${m.label} preferred`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Checkbox
                      checked={m.is_active}
                      onChange={(e) => toggle(m, { is_active: e.target.checked })}
                      aria-label={`${m.label} active`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button variant="ghost" size="icon" onClick={() => setEditing({ ...m })} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleting(m)} className="text-muted-foreground hover:text-destructive" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">
                    No models yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AnalysisModelFormDialog
          draft={editing}
          onChange={setEditing}
          onCancel={() => {
            setEditing(null);
            setFormError(null);
          }}
          onSave={handleSave}
          saving={saving}
          error={formError}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete analysis model?"
        description={`"${deleting?.label}" will be removed from the run picker. Analysis already generated with it is unaffected. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
